/**
 * One month's ribbon of day cells, as data — WP 6.5's month view draws a
 * ribbon per month (Berlin or Away, a holiday, a visitor stripe, today), and
 * `npm test` cannot see a rendered strip of coloured divs. It can see that a
 * Saturday inside a trip is `away` and `weekend` both, which is what this
 * file hands back instead of JSX.
 *
 * No imports, the same reason `yearItems.ts` and `timelineLayout.ts` have
 * none: the root suite's Node test runner loads this file directly and will
 * not resolve an extensionless specifier the way Vite does. Every shape below
 * is duck-typed rather than imported from `calendar.ts` or `types.ts` — the
 * caller (`MonthRibbon.tsx`) already has `periods()` and `holidayOn()` in
 * scope and passes their output in, rather than this file re-deriving either.
 */

export interface RibbonPeriod {
  place: string
  start: string
  end: string
}

/** Structurally what `Visitor` (`types.ts`) satisfies. */
export interface RibbonVisitor {
  starts_on: string
  ends_on: string
  at_mine: boolean
}

export interface RibbonHoliday {
  /** ISO date. */
  on: string
  name: string
}

export interface RibbonCell {
  /** ISO date. */
  on: string
  /** True for the second home or Travel — the ribbon draws two colours, not three
   *  (brief: "Berlin or Away, the second home is Away"). */
  away: boolean
  /** Saturday or Sunday, drawn with a darker foot. */
  weekend: boolean
  /** The holiday's name, or null. */
  holiday: string | null
  /** A visitor staying at yours covers this day — the gold stripe. A visitor
   *  staying elsewhere still gets a `VisitorCard` in the stack below, just
   *  not this: the legend calls the stripe "Visitor at yours" precisely. */
  visitorStripe: boolean
  today: boolean
  /** Before the visible window's start (`ctx.lo`) — the current month can
   *  open partway through (from today, WP 8.5), and the days before that
   *  still have to be in the grid for the weekday columns to line up, just
   *  drawn quietly. */
  out: boolean
}

export interface RibbonContext {
  /** The earliest day considered "in" the visible window; a cell before it
   *  is `out` rather than left off the grid. */
  lo: string
  today: string
  /** Whatever range they cover — the caller computes this once for the whole
   *  visible span and every month slices its own days out of it. */
  periods: readonly RibbonPeriod[]
  visitors: readonly RibbonVisitor[]
  /** Pre-resolved for the month's own days, since which country's calendar
   *  applies can change day to day (a trip mid-month) and only the caller,
   *  which already has `periods`, knows which. */
  holidays: readonly RibbonHoliday[]
}

const D = (s: string) => new Date(`${s}T12:00:00Z`)
const addDays = (s: string, n: number) => new Date(D(s).getTime() + n * 86400000).toISOString().slice(0, 10)
/** Monday-first: 0 Mon … 6 Sun, matching `calendar.ts`'s own `dow`-shaped
 *  callers (the weekday letter row starts on Monday). */
const dow = (s: string) => (D(s).getUTCDay() + 6) % 7
const monthEnd = (ym: string) => {
  const [y, m] = ym.split('-').map(Number)
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10)
}

/**
 * Whether a day the ribbon would otherwise colour Away is one of the two
 * edge days a stay and a visit only touch at, rather than truly overlap —
 * `VisitorCard.tsx`'s own strict-interior check (`p.end > v.starts_on &&
 * p.start < v.ends_on`), applied per day instead of per period. WP 6.5's
 * design pass found the ribbon disagreeing with the card sitting right
 * below it: the day a trip ends and a visitor arrives (or a visitor leaves
 * and the next trip starts), `VisitorCard` already says "staying at yours"
 * with no away qualifier — the strict check decided that edge doesn't
 * count — but the ribbon painted the same day Away *and* striped it gold,
 * which is both a contradiction and, composed, the two colours' worst
 * contrast pairing (2.44:1 in light mode, measured). A true multi-day
 * overlap (a visitor arriving mid-trip) is not this case — the strict check
 * still says "away", so both signals still show, correctly, for the
 * days that really are both.
 */
function isEdgeExempt(period: RibbonPeriod, d: string, visitors: readonly RibbonVisitor[]): boolean {
  return visitors.some(v => v.at_mine
    && !(period.end > v.starts_on && period.start < v.ends_on)
    && (d === period.end || d === period.start))
}

/** One month, `'YYYY-MM'`, as a run of day cells from its 1st to its last. */
export function ribbonCells(ym: string, ctx: RibbonContext): RibbonCell[] {
  const first = `${ym}-01`
  const last = monthEnd(ym)
  const holidayByDay = new Map(ctx.holidays.map(h => [h.on, h.name]))
  const cells: RibbonCell[] = []
  for (let d = first; d <= last; d = addDays(d, 1)) {
    const period = ctx.periods.find(p => d >= p.start && d <= p.end)
    const w = dow(d)
    const away = !!period && period.place !== 'Home' && !isEdgeExempt(period, d, ctx.visitors)
    cells.push({
      on: d,
      away,
      weekend: w >= 5,
      holiday: holidayByDay.get(d) ?? null,
      visitorStripe: ctx.visitors.some(v => v.at_mine && d >= v.starts_on && d <= v.ends_on),
      today: d === ctx.today,
      out: d < ctx.lo,
    })
  }
  return cells
}
